import { z } from 'zod';

export const CustomerOriginSchema = z.url().max(300).refine(value => { const u = new URL(value); return !u.username && !u.password && !u.search && !u.hash && u.pathname === '/' && (u.protocol === 'https:' || (u.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(u.hostname))); }, 'Use an HTTPS customer origin, without a path');

export const MODULES = [
  { id: 'snagging', name: 'Snagging', availability: 'released' },
  { id: 'reporting', name: 'Site reporting', availability: 'planned' },
  { id: 'diary', name: 'Site Diary & Conversations', availability: 'planned' },
  { id: 'safety', name: 'Safety and permits', availability: 'planned' },
] as const;
export const LicenceSchema = z.object({
  status: z.enum(['active', 'notice', 'grace', 'restricted', 'closed']),
  startsAt: z.iso.datetime(), renewalAt: z.iso.datetime(), endsAt: z.iso.datetime(), graceUntil: z.iso.datetime(),
  modules: z.array(z.enum(['snagging'])).max(1),
}).strict().refine(value => Date.parse(value.startsAt) <= Date.parse(value.renewalAt) && Date.parse(value.renewalAt) <= Date.parse(value.endsAt) && Date.parse(value.endsAt) <= Date.parse(value.graceUntil), 'Licence dates must be in order');
export type Licence = z.infer<typeof LicenceSchema>;
export const AdministratorActionSchema = z.object({
  id: z.uuid(), kind: z.enum(['invite', 'recover', 'deactivate', 'revoke_invitation']),
  email: z.email().max(254), name: z.string().trim().min(1).max(120),
  tokenHash: z.string().regex(/^[a-f0-9]{64}$/).optional(), expiresAt: z.iso.datetime().optional(),
  replaceUserId: z.uuid().optional(), invitationId: z.uuid().optional(),
  authorisation: z.string().trim().min(8).max(500),
}).strict();
export const ManagementDocumentSchema = z.object({
  format: z.literal(1), customerId: z.uuid(), installationId: z.uuid(), organisationId: z.uuid(),
  origin: CustomerOriginSchema,
  customerName: z.string().trim().min(1).max(160), slug: z.string().regex(/^[a-z][a-z0-9-]{2,50}$/),
  revision: z.number().int().positive(), issuedAt: z.iso.datetime(), applyBefore: z.iso.datetime(),
  licence: LicenceSchema, securityRevoked: z.boolean(),
  administratorActions: z.array(AdministratorActionSchema).max(100),
  actor: z.string().min(1).max(254), reason: z.string().trim().min(8).max(500),
}).strict();
export type ManagementDocument = z.infer<typeof ManagementDocumentSchema>;
export type AdministratorAction = z.infer<typeof AdministratorActionSchema>;

export function licenceAccess(licence: Licence | null, now = Date.now()) {
  if (!licence) return { status: 'restricted', write: false, read: true, export: true, message: 'Waiting for an activated licence. Existing records remain available.' };
  let status: Licence['status'] = licence.status;
  if (status !== 'restricted' && status !== 'closed') {
    if (now >= Date.parse(licence.graceUntil)) status = 'restricted';
    else if (now >= Date.parse(licence.endsAt)) status = 'grace';
    else if (now >= Date.parse(licence.renewalAt) && status === 'active') status = 'notice';
  }
  const write = !['restricted', 'closed'].includes(status) && now >= Date.parse(licence.startsAt) && licence.modules.includes('snagging');
  return { status, write, read: true, export: true, message: write
    ? status === 'active' ? 'Licence active' : `Licence ${status}. Contact your administrator about renewal.`
    : 'Read and export remain available. Changes stay on this device until your licence is reactivated.' };
}

export type MfaChallenge = { mfaRequired: true; challenge: string; setup?: { secret: string; uri: string } };

export const CustomerInputSchema = z.object({
  name: z.string().trim().min(1).max(160), slug: z.string().regex(/^[a-z][a-z0-9-]{2,50}$/),
  contactName: z.string().trim().min(1).max(120), contactEmail: z.email().max(254),
  origin: CustomerOriginSchema,
  licence: LicenceSchema, reason: z.string().trim().min(8).max(500),
}).strict();
export type CustomerInput = z.infer<typeof CustomerInputSchema>;
export type OperationalRecord = { id: string; kind: 'service' | 'backup' | 'restore' | 'release' | 'provisioning'; outcome: 'passed' | 'failed' | 'unknown'; reference: string; observedAt: string; recordedAt: string; recordedBy: string; verification: 'operator-reported' };
export type Customer = Omit<CustomerInput, 'reason'> & {
  id: string; customerNumber: string; installationId: string; organisationId: string; revision: number; createdAt: string; updatedAt: string;
  securityRevoked: boolean; administratorActions: AdministratorAction[]; operations: OperationalRecord[];
  document: ManagementDocument;
  setup?: CustomerSetup;
};

export const formatCustomerNumber = (number: string | number) => `SM-${String(number).padStart(6, '0')}`;

export type CustomerSetup = {
  status: 'queued' | 'provisioning' | 'live' | 'problem';
  stage: string; requestedAt: string; updatedAt: string;
  appliedRevision: number; verifiedAt?: string; problem?: string;
};

export const CUSTOMER_DOMAIN = 'site-method.com';
export const RESERVED_CUSTOMER_NAMES = new Set(['owner', 'www', 'app', 'api', 'admin', 'mail', 'email', 'smtp', 'imap', 'pop', 'support', 'help', 'login', 'auth', 'accounts', 'status', 'assets', 'cdn', 'static', 'portal', 'billing', 'autodiscover', 'ftp', 'webmail', 'ns1', 'ns2']);
export function customerAddressName(company: string) {
  return company.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/(?:[\s,.]+(?:ltd|limited|llp|plc)\.?)+\s*$/i, '').replace(/[^a-z0-9]/g, '').slice(0, 51);
}
export function validCustomerAddressName(name: string) {
  return /^[a-z][a-z0-9-]{1,49}[a-z0-9]$/.test(name) && !RESERVED_CUSTOMER_NAMES.has(name);
}
export const customerAddress = (name: string) => `https://${name}.${CUSTOMER_DOMAIN}`;
export function managedCustomerAddress(origin: string) {
  try {
    const url = new URL(origin), suffix = `.${CUSTOMER_DOMAIN}`;
    const name = url.hostname.endsWith(suffix) ? url.hostname.slice(0, -suffix.length) : '';
    return url.protocol === 'https:' && !url.port && !url.username && !url.password && url.pathname === '/' && !url.search && !url.hash && validCustomerAddressName(name);
  } catch { return false; }
}
export function addressAvailability(name: string, customers: Pick<Customer, 'origin' | 'slug'>[]) {
  const occupied = (value: string) => customers.some(customer => customer.slug === value || new URL(customer.origin).origin.toLowerCase() === customerAddress(value));
  const valid = validCustomerAddressName(name), available = valid && !occupied(name);
  const alternatives: string[] = [];
  if (valid && !available) for (let number = 2; number < 100 && alternatives.length < 3; number++) {
    const alternative = `${name.slice(0, 48)}${number}`;
    if (!occupied(alternative)) alternatives.push(alternative);
  }
  return { name, origin: customerAddress(name), available, alternatives,
    message: !valid ? 'Choose 3–51 lowercase letters or numbers, starting with a letter. This name may be reserved.' : !available ? 'That address is already in use. Choose an alternative below.' : 'This address is available.' };
}

export function customerSetupLabel(customer: Pick<Customer, 'setup'>) {
  const setup = customer.setup;
  if (!setup) return 'Awaiting setup';
  if (setup.status === 'live') return 'Live';
  if (setup.status === 'problem') return 'Needs attention';
  if (setup.appliedRevision > 0) return 'Updating';
  return setup.status === 'queued' ? 'Waiting to start' : 'Setting up';
}
export function customerLicenceLabel(customer: Pick<Customer, 'licence' | 'securityRevoked'>, now = Date.now()) {
  if (customer.securityRevoked) return 'Security suspended';
  if (customer.licence.status === 'closed') return 'Closed';
  if (customer.licence.status === 'restricted') return 'Suspended';
  if (now < Date.parse(customer.licence.startsAt)) return 'Starts soon';
  if (now >= Date.parse(customer.licence.graceUntil)) return 'Expired';
  if (now >= Date.parse(customer.licence.endsAt)) return 'Grace period';
  if (licenceAccess(customer.licence, now).status === 'notice') return 'Renewal due';
  return customer.licence.modules.includes('snagging') ? 'Active' : 'No modules enabled';
}
